import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../../HandleAPICalls/actions";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CreateRuleComponent from "./component";
import Sidebar from "../../../../Navigation/Sidebar/Sidebar";

// Import Modal components
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from "@mui/material/Typography";
import Button from '@mui/material/Button';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const CreateRuleContainer = (props) => {
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetchingDepartments, setFetchingDepartments] = useState(false);
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Corporate ID - get from multiple possible sources
  const [corporateId, setCorporateId] = useState(null);

  // Department data
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Recharge type data
  const [resetEnabled, setResetEnabled] = useState(false);
  const [carryForwardEnabled, setCarryForwardEnabled] = useState(false);

  // Reset configuration
  const [resetConfig, setResetConfig] = useState({
    amount: "",
    frequency: "MONTHLY",
    day: 2,
  });

  // Carry forward configuration
  const [carryForwardConfig, setCarryForwardConfig] = useState({
    amount: "",
    lowBalance: "",
    frequency: "MONTHLY",
    day: 1,
  });

  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  // Function to extract corporateId from various sources
  const extractCorporateId = () => {
    // 1. First try to get from URL params
    if (params.corporateId) {
      console.log("Found corporateId in params:", params.corporateId);
      return parseInt(params.corporateId);
    }

    // 2. Try to get from URL query parameters
    const queryParams = new URLSearchParams(location.search);
    const queryCorporateId = queryParams.get('corporateId');
    if (queryCorporateId) {
      console.log("Found corporateId in query params:", queryCorporateId);
      return parseInt(queryCorporateId);
    }

    // 3. Try to get from localStorage
    const storedCorporateId = localStorage.getItem('corporateId') || localStorage.getItem('currentCorporateId');
    if (storedCorporateId) {
      console.log("Found corporateId in localStorage:", storedCorporateId);
      return parseInt(storedCorporateId);
    }

    console.warn("Corporate ID not found in any source");
    return null;
  };

  useEffect(() => {
    const extractedId = extractCorporateId();
    console.log("Extracted Corporate ID:", extractedId);
    
    if (extractedId) {
      setCorporateId(extractedId);
      handleGetDepartmentsList(extractedId);
    } else {
      toast.error("Corporate ID not found. Please select a corporate first.");
      navigate("/home/corporate/list");
    }
  }, []);

  // Fetch departments from API - FIXED: Only show API departments, no default
  const handleGetDepartmentsList = (id) => {
    if (!id) {
      console.error("No corporateId provided to handleGetDepartmentsList");
      toast.error("Corporate ID is missing");
      return;
    }

    setFetchingDepartments(true);
    console.log("Fetching departments for corporate ID:", id);
    
    props.getDataFromAPI(
      `/partner/api/v1/corp/${id}/details`,
      "GET",
      undefined,
      (response) => {
        setFetchingDepartments(false);
        console.log("Departments API Full Response:", response);
        
        // Extract departments from response
        let departmentsArray = [];
        
        // Check different possible response structures
        if (response?.data?.departments && Array.isArray(response.data.departments)) {
          // Structure: { data: { departments: [...] } }
          departmentsArray = response.data.departments;
          console.log("Found departments in response.data.departments");
        } else if (response?.departments && Array.isArray(response.departments)) {
          // Structure: { departments: [...] }
          departmentsArray = response.departments;
          console.log("Found departments in response.departments");
        } else if (Array.isArray(response)) {
          // Structure: [...] (direct array)
          departmentsArray = response;
          console.log("Response is direct array");
        } else if (response?.data && Array.isArray(response.data)) {
          // Structure: { data: [...] }
          departmentsArray = response.data;
          console.log("Found departments in response.data");
        }
        
        console.log("Extracted departments array:", departmentsArray);
        
        if (departmentsArray.length > 0) {
          const formattedDepartments = departmentsArray.map(dept => ({
            id: dept.departmentId || dept.id || dept.department_id,
            name: dept.departmentName || dept.name || dept.department_name,
            totalEmployees: dept.totalEmployees,
            initialBalance: dept.initialBalance
          })).filter(dept => dept.id && dept.name);
          
          console.log("Formatted departments:", formattedDepartments);
          
          // Set only API departments - NO DEFAULT DEPARTMENT
          setAvailableDepartments(formattedDepartments);
          
          // DO NOT auto-select any department
          setSelectedDepartment(null);
          
          if (formattedDepartments.length === 0) {
            toast.warning("No departments available for this corporate");
          }
        } else {
          console.warn("No departments found in API response");
          setAvailableDepartments([]);
          setSelectedDepartment(null);
          toast.warning("No departments available for this corporate");
        }
      },
      (err) => {
        setFetchingDepartments(false);
        console.error("Failed to fetch departments:", err);
        toast.error(err.message || "Failed to fetch departments");
        setAvailableDepartments([]);
        setSelectedDepartment(null);
      }
    );
  };

  // Filter departments based on search term
  const getFilteredDepartments = () => {
    if (!searchTerm.trim()) {
      return availableDepartments;
    }
    
    return availableDepartments.filter(dept =>
      dept.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dept.id.toString().includes(searchTerm)
    );
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!selectedDepartment || selectedDepartment.id === undefined) {
        toast.error("Please select a department");
        return;
      }
      setActiveStep((prevStep) => prevStep + 1);
      
    } else if (activeStep === 1) {
      if (!resetEnabled && !carryForwardEnabled) {
        toast.error("Please select at least one balance handling type");
        return;
      }
      setActiveStep((prevStep) => prevStep + 1);
      
    } else if (activeStep === 2) {
      handleFinalSubmit();
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      navigate(-1);
    } else {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const handleDepartmentSelect = (department) => {
    setSelectedDepartment(department);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleTypeChange = (type, isChecked) => {
    if (type === "reset") {
      setResetEnabled(isChecked);
      if (!isChecked) {
        setResetConfig({
          amount: "",
          frequency: "MONTHLY",
          day: 2,
        });
      }
    } else if (type === "carryForward") {
      setCarryForwardEnabled(isChecked);
      if (!isChecked) {
        setCarryForwardConfig({
          amount: "",
          lowBalance: "",
          frequency: "MONTHLY",
          day: 2,
        });
      }
    }
  };

  const handleResetConfigChange = (field, value) => {
    setResetConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCarryForwardConfigChange = (field, value) => {
    setCarryForwardConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleResetFrequencyChange = (frequency) => {
    setResetConfig(prev => ({
      ...prev,
      frequency: frequency,
      day: frequency === "DAILY" ? null : 2
    }));
  };

  const handleCarryForwardFrequencyChange = (frequency) => {
    setCarryForwardConfig(prev => ({
      ...prev,
      frequency: frequency,
      day: frequency === "DAILY" ? null : 2
    }));
  };

  const handleResetDayChange = (day) => {
    setResetConfig(prev => ({
      ...prev,
      day: day
    }));
  };

  const handleCarryForwardDayChange = (day) => {
    setCarryForwardConfig(prev => ({
      ...prev,
      day: day
    }));
  };

  const handleResetDateSelect = (date) => {
    setResetConfig(prev => ({
      ...prev,
      day: date
    }));
  };

  const handleCarryForwardDateSelect = (date) => {
    setCarryForwardConfig(prev => ({
      ...prev,
      day: date
    }));
  };

  const validateForm = () => {
    let errors = [];

    if (!corporateId) {
      errors.push("Corporate ID is missing. Please select a corporate first.");
    }

    if (!selectedDepartment || selectedDepartment.id === undefined) {
      errors.push("Please select a department");
    }

    if (!resetEnabled && !carryForwardEnabled) {
      errors.push("Please select at least one balance handling type");
    }

    if (resetEnabled) {
      if (!resetConfig.amount || resetConfig.amount <= 0) {
        errors.push("Please enter a valid recharge amount for Reset");
      }
      if (!resetConfig.frequency) {
        errors.push("Please select a frequency for Reset");
      }
      if (resetConfig.frequency !== "DAILY" && !resetConfig.day) {
        errors.push("Please select a reset day/date for Reset");
      }
    }

    if (carryForwardEnabled) {
      if (!carryForwardConfig.amount || carryForwardConfig.amount <= 0) {
        errors.push("Please enter a valid recharge amount for Carry Forward");
      }
      if (carryForwardConfig.lowBalance < 0) {
        errors.push("Low balance limit cannot be negative");
      }
      if (!carryForwardConfig.frequency) {
        errors.push("Please select a frequency for Carry Forward");
      }
      if (carryForwardConfig.frequency !== "DAILY" && !carryForwardConfig.day) {
        errors.push("Please select a reset day/date for Carry Forward");
      }
    }

    return errors;
  };

  const handleFinalSubmit = () => {
    const errors = validateForm();
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
      return;
    }

    setLoading(true);
    
    const payload = {
      corporateId: corporateId,
      departmentId: selectedDepartment.id,
      reset: resetEnabled ? {
        enabled: true,
        amount: parseFloat(resetConfig.amount),
        frequency: resetConfig.frequency,
        day: resetConfig.frequency !== "DAILY" ? parseInt(resetConfig.day) : null
      } : null,
      carryForward: carryForwardEnabled ? {
        enabled: true,
        amount: parseFloat(carryForwardConfig.amount),
        lowBalance: parseFloat(carryForwardConfig.lowBalance) || 0,
        frequency: carryForwardConfig.frequency,
        day: carryForwardConfig.frequency !== "DAILY" ? parseInt(carryForwardConfig.day) : null
      } : null
    };

    console.log("API Payload:", JSON.stringify(payload, null, 2));

    props.getDataFromAPI(
      `/partner/api/v1/recharge-rules`,
      "POST",
      payload,
      (response) => {
        setLoading(false);
        console.log("API Response:", response);
        if (response) {
          setSuccessMessage(`Recharge rule for ${selectedDepartment.name} department has been successfully created!`);
          setShowSuccessModal(true);
          toast.success("Recharge rule created successfully!");
        } else {
          toast.error("Failed to create recharge rule");
        }
      },
      (err) => {
        setLoading(false);
        console.error("Failed to create recharge rule:", err);
        toast.error(err.message || "Failed to create recharge rule");
      }
    );
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate(-1);
  };

  return (
    <div className="create-rule-layout">
      <Sidebar />

      <div className="create-rule-content">
        <div className="create-rule-header">
          <Button 
            onClick={handleBack} 
            startIcon={<ArrowBackIcon />}
            sx={{
              color: '#666',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            
          </Button>
          <h2 className="create-rule-title">Create a New Rule</h2>
          {corporateId && (
            <div className="corporate-id-display">
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
                Corporate ID: {corporateId}
              </Typography>
            </div>
          )}
        </div>
        
        <ToastContainer 
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
        
        <div className="create-rule-main">
          {!corporateId ? (
            <div className="no-corporate-message">
              <Typography variant="h6" sx={{ color: '#666', textAlign: 'center', mb: 2 }}>
                Corporate ID not found
              </Typography>
              <Typography variant="body2" sx={{ color: '#999', textAlign: 'center', mb: 3 }}>
                Please select a corporate first before creating rules.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/home/corporate/list")}
                sx={{
                  display: 'block',
                  margin: '0 auto',
                  backgroundColor: '#3f51b5',
                  '&:hover': {
                    backgroundColor: '#303f9f',
                  }
                }}
              >
                Go to Corporate List
              </Button>
            </div>
          ) : (
            <CreateRuleComponent
              activeStep={activeStep}
              handleNext={handleNext}
              handleBack={handleBack}
              selectedDepartment={selectedDepartment}
              handleDepartmentSelect={handleDepartmentSelect}
              availableDepartments={getFilteredDepartments()}
              fetchingDepartments={fetchingDepartments}
              searchTerm={searchTerm}
              handleSearchChange={handleSearchChange}
              resetEnabled={resetEnabled}
              carryForwardEnabled={carryForwardEnabled}
              handleTypeChange={handleTypeChange}
              resetConfig={resetConfig}
              handleResetConfigChange={handleResetConfigChange}
              handleResetFrequencyChange={handleResetFrequencyChange}
              handleResetDayChange={handleResetDayChange}
              handleResetDateSelect={handleResetDateSelect}
              carryForwardConfig={carryForwardConfig}
              handleCarryForwardConfigChange={handleCarryForwardConfigChange}
              handleCarryForwardFrequencyChange={handleCarryForwardFrequencyChange}
              handleCarryForwardDayChange={handleCarryForwardDayChange}
              handleCarryForwardDateSelect={handleCarryForwardDateSelect}
              loading={loading}
            />
          )}
        </div>
      </div>

      {/* Success Modal */}
      <Dialog
        open={showSuccessModal}
        onClose={handleSuccessModalClose}
        aria-labelledby="success-dialog-title"
        aria-describedby="success-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: '20px',
            maxWidth: '550px',
            width: '90%'
          }
        }}
      >
        <DialogTitle 
          id="success-dialog-title"
          sx={{ 
            textAlign: 'center',
            paddingTop: '40px',
            paddingBottom: '10px'
          }}
        >
          <CheckCircleIcon 
            sx={{ 
              fontSize: 80, 
              color: '#14AE78',
              marginBottom: '16px'
            }} 
          />
          <div style={{ fontSize: '24px', fontWeight: 600, color: '#333' }}>
            Success!
          </div>
        </DialogTitle>
        <DialogContent>
          <DialogContentText 
            id="success-dialog-description"
            sx={{ 
              textAlign: 'center',
              fontSize: '16px',
              color: '#666',
              lineHeight: '1.6',
              marginBottom: '16px'
            }}
          >
            {successMessage}
          </DialogContentText>
          
          <div style={{ 
            backgroundColor: '#f8f9fa', 
            borderRadius: '10px', 
            padding: '16px', 
            margin: '20px 0',
            border: '1px solid #e8e8e8'
          }}>
            <div style={{ 
              fontSize: '14px', 
              color: '#666', 
              marginBottom: '8px',
              fontWeight: 500
            }}>
              Corporate ID:
            </div>
            <div style={{ 
              fontSize: '16px', 
              fontWeight: '600',
              color: '#333',
              padding: '8px 12px',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #ddd',
              fontFamily: 'monospace'
            }}>
              {corporateId}
            </div>
            
            <div style={{ 
              fontSize: '14px', 
              color: '#666', 
              marginBottom: '8px',
              fontWeight: 500,
              marginTop: '12px'
            }}>
              Department:
            </div>
            <div style={{ 
              fontSize: '16px', 
              fontWeight: '600',
              color: '#333',
              padding: '8px 12px',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #ddd'
            }}>
              {selectedDepartment?.name} (ID: {selectedDepartment?.id})
            </div>
          </div>
          
          <DialogContentText 
            sx={{ 
              textAlign: 'center',
              fontSize: '14px',
              color: '#999',
              marginTop: '16px'
            }}
          >
            You will be redirected to the rules management page.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', padding: '24px' }}>
          <Button
            onClick={handleSuccessModalClose}
            variant="contained"
            sx={{
              backgroundColor: '#14AE78',
              color: 'white',
              borderRadius: '10px',
              padding: '12px 48px',
              fontSize: '15px',
              fontWeight: 500,
              textTransform: 'none',
              minWidth: '180px',
              '&:hover': {
                backgroundColor: '#0f8c65',
                boxShadow: '0 4px 12px rgba(20, 174, 120, 0.3)'
              }
            }}
          >
            Go to Rules Management
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    currentCorporate: state.corporate?.currentCorporate || 
                     state.auth?.currentCorporate || 
                     state.user?.currentCorporate,
    props: state,
  };
}

export default connect(mapStateToProps, {
  getDataFromAPI,
})(CreateRuleContainer);